import React, { useState } from 'react'
import { Check, Zap, Gift } from 'lucide-react'
import { Link } from 'react-router-dom'

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly')

  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for small businesses and startups',
      monthlyPrice: 9.99,
      annualPrice: 95.90,
      features: [
        '1 AI Chatbot',
        '500 conversations/month',
        'Basic analytics',
        'Email support',
        '5 integrations',
        'Multi-language support',
        'Mobile app access',
        'Knowledge base'
      ],
      color: 'blue',
      popular: false,
      freeTrial: false
    },
    {
      name: 'Pro',
      description: 'For growing businesses with higher volume',
      monthlyPrice: 29.99,
      annualPrice: 287.90,
      features: [
        '3 AI Chatbots',
        '2 Voice Calling Agents',
        '5,000 conversations/month',
        'Advanced analytics & reports',
        'Priority email & chat support',
        'Unlimited integrations',
        'Custom branding',
        'API access',
        'Team collaboration (5 users)',
        'Advanced automation'
      ],
      color: 'purple',
      popular: true,
      freeTrial: true
    },
    {
      name: 'Enterprise',
      description: 'For large organizations with custom needs',
      monthlyPrice: 49.99,
      annualPrice: 479.90,
      features: [
        'Unlimited AI Chatbots',
        'Unlimited Voice Agents',
        'Unlimited conversations',
        'Enterprise analytics & BI',
        'Dedicated account manager',
        'Custom integrations',
        'White-label solution',
        'Advanced security & compliance',
        'Unlimited team members',
        'SLA guarantee',
        'Custom AI training',
        'On-premise deployment option'
      ],
      color: 'indigo',
      popular: false,
      freeTrial: false
    }
  ]

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice
  }

  const getSavings = (plan) => {
    const monthlyCost = plan.monthlyPrice * 12
    const annualCost = plan.annualPrice
    const savings = ((monthlyCost - annualCost) / monthlyCost * 100).toFixed(0)
    return savings
  }

  return (
    <main data-component-path="client/src/pages/PricingPage.jsx" data-component-line="95:4" data-component-id="client/src/pages/PricingPage.jsx:95:4" className="pt-20 bg-gray-50 min-h-screen">
      <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="96:6" data-component-id="client/src/pages/PricingPage.jsx:96:6" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="97:8" data-component-id="client/src/pages/PricingPage.jsx:97:8" className="text-center mb-16">
          <h1 data-component-path="client/src/pages/PricingPage.jsx" data-component-line="98:10" data-component-id="client/src/pages/PricingPage.jsx:98:10" className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h1>
          <p data-component-path="client/src/pages/PricingPage.jsx" data-component-line="101:10" data-component-id="client/src/pages/PricingPage.jsx:101:10" className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Choose the perfect plan for your business. All plans include a 14-day free trial.
          </p>

          <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="106:10" data-component-id="client/src/pages/PricingPage.jsx:106:10" className="inline-flex items-center bg-white rounded-lg p-1 shadow-md">
            <button data-component-path="client/src/pages/PricingPage.jsx" data-component-line="107:12" data-component-id="client/src/pages/PricingPage.jsx:107:12"
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button data-component-path="client/src/pages/PricingPage.jsx" data-component-line="117:12" data-component-id="client/src/pages/PricingPage.jsx:117:12"
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                billingCycle === 'annual'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span data-component-path="client/src/pages/PricingPage.jsx" data-component-line="125:14" data-component-id="client/src/pages/PricingPage.jsx:125:14" className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="132:8" data-component-id="client/src/pages/PricingPage.jsx:132:8" className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="134:12" data-component-id="client/src/pages/PricingPage.jsx:134:12"
              key={index}
              className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                plan.popular ? 'ring-2 ring-blue-600 scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="141:16" data-component-id="client/src/pages/PricingPage.jsx:141:16" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center py-2 font-semibold flex items-center justify-center gap-2">
                  <Zap data-component-path="client/src/pages/PricingPage.jsx" data-component-line="142:18" data-component-id="client/src/pages/PricingPage.jsx:142:18" className="w-4 h-4" />
                  Most Popular
                </div>
              )}

              <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="147:14" data-component-id="client/src/pages/PricingPage.jsx:147:14" className="p-8">
                <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="148:16" data-component-id="client/src/pages/PricingPage.jsx:148:16" className="flex items-center justify-between mb-2">
                  <h3 data-component-path="client/src/pages/PricingPage.jsx" data-component-line="149:18" data-component-id="client/src/pages/PricingPage.jsx:149:18" className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  {plan.freeTrial && (
                    <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="151:20" data-component-id="client/src/pages/PricingPage.jsx:151:20" className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                      <Gift data-component-path="client/src/pages/PricingPage.jsx" data-component-line="152:22" data-component-id="client/src/pages/PricingPage.jsx:152:22" className="w-3 h-3" />
                      1 Month Free
                    </div>
                  )}
                </div>
                <p data-component-path="client/src/pages/PricingPage.jsx" data-component-line="157:16" data-component-id="client/src/pages/PricingPage.jsx:157:16" className="text-gray-600 mb-6">{plan.description}</p>

                <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="159:16" data-component-id="client/src/pages/PricingPage.jsx:159:16" className="mb-6">
                  <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="160:18" data-component-id="client/src/pages/PricingPage.jsx:160:18" className="flex items-baseline gap-2">
                    <span data-component-path="client/src/pages/PricingPage.jsx" data-component-line="161:20" data-component-id="client/src/pages/PricingPage.jsx:161:20" className="text-5xl font-bold text-gray-900">
                      ${getPrice(plan).toFixed(2)}
                    </span>
                    <span data-component-path="client/src/pages/PricingPage.jsx" data-component-line="164:20" data-component-id="client/src/pages/PricingPage.jsx:164:20" className="text-gray-600">
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p data-component-path="client/src/pages/PricingPage.jsx" data-component-line="169:20" data-component-id="client/src/pages/PricingPage.jsx:169:20" className="text-sm text-green-600 mt-2">
                      Save {getSavings(plan)}% compared to monthly
                    </p>
                  )}
                </div>

                <Link data-component-path="client/src/pages/PricingPage.jsx" data-component-line="175:16" data-component-id="client/src/pages/PricingPage.jsx:175:16"
                  to="/signup"
                  className={`w-full h-11 px-6 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg font-medium mb-8 ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.freeTrial ? 'Start Free Month' : 'Start Free Trial'}
                </Link>

                <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="186:16" data-component-id="client/src/pages/PricingPage.jsx:186:16" className="space-y-4">
                  <p data-component-path="client/src/pages/PricingPage.jsx" data-component-line="187:18" data-component-id="client/src/pages/PricingPage.jsx:187:18" className="font-semibold text-gray-900 mb-4">What's included:</p>
                  {plan.features.map((feature, featureIndex) => (
                    <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="189:20" data-component-id="client/src/pages/PricingPage.jsx:189:20" key={featureIndex} className="flex items-start gap-3">
                      <Check data-component-path="client/src/pages/PricingPage.jsx" data-component-line="190:22" data-component-id="client/src/pages/PricingPage.jsx:190:22" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span data-component-path="client/src/pages/PricingPage.jsx" data-component-line="191:22" data-component-id="client/src/pages/PricingPage.jsx:191:22" className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div data-component-path="client/src/pages/PricingPage.jsx" data-component-line="200:8" data-component-id="client/src/pages/PricingPage.jsx:200:8" className="bg-blue-600 rounded-2xl p-12 text-center text-white">
          <h2 data-component-path="client/src/pages/PricingPage.jsx" data-component-line="201:10" data-component-id="client/src/pages/PricingPage.jsx:201:10" className="text-3xl md:text-4xl font-bold mb-4">
            Need a Custom Solution?
          </h2>
          <p data-component-path="client/src/pages/PricingPage.jsx" data-component-line="204:10" data-component-id="client/src/pages/PricingPage.jsx:204:10" className="text-xl mb-8 text-blue-100">
            We offer tailored enterprise solutions with custom pricing, dedicated support, and specialized features.
          </p>
          <Link data-component-path="client/src/pages/PricingPage.jsx" data-component-line="207:10" data-component-id="client/src/pages/PricingPage.jsx:207:10"
            to="/#contact"
            className="inline-flex h-14 px-8 bg-white text-blue-600 rounded-lg hover:bg-gray-100 items-center justify-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold text-lg"
          >
            Contact Sales
          </Link>
        </div>
      </div>
    </main>
  )
}

export default PricingPage